/**
 * The project's saved Transform pipelines, for the dashboard's preparation.
 * Pipelines are saved queries of kind "pipeline" (the console's Transform
 * records). The list endpoint carries each record's steps but not its
 * script, so each pipeline's record is read for the script: the script is
 * what runs, the steps only describe it.
 */
import { useQuery } from "@tanstack/react-query"
import { queryKeys } from "api/queryKeys"
import { useSavedQueries } from "features/QueryConsole/api/useSavedQueries"
import type { TransformPipeline } from "types/queryBuilder.types"
import type {
  PipelineMeta,
  SavedQueryEnvelope,
} from "types/savedQuery.types"

/** A saved pipeline: its name, its steps (for display) and the script it runs. */
export interface SavedPipeline {
  name: string
  pipeline: TransformPipeline
  script: string
}

/** Same freshness as the console's saved-query list. */
const PIPELINES_STALE_MS = 5_000

/**
 * Under the project's saved-query key, so anything that refreshes the
 * project's saved queries refreshes this too. Not the list key itself: that
 * cache holds the envelopes the console reads.
 */
export const pipelinesQueryKey = (projectId: string, owner: string) =>
  [...queryKeys.savedQueries.project(projectId, owner), "pipelines"] as const

const isPipeline = (
  e: SavedQueryEnvelope
): e is SavedQueryEnvelope & { meta: PipelineMeta } =>
  e.meta.kind === "pipeline"

/**
 * The project's saved pipelines in the order the list endpoint gives them.
 * `enabled` false keeps it from fetching (a dashboard that applies no
 * pipeline does not need them to start).
 */
export const usePipelines = (
  projectId: string,
  owner: string,
  enabled = true
) => {
  const { listStructured, getStructured } = useSavedQueries()
  return useQuery({
    queryKey: pipelinesQueryKey(projectId, owner),
    enabled: enabled && Boolean(projectId && owner),
    staleTime: PIPELINES_STALE_MS,
    meta: { errorTitle: "Could not load the saved pipelines" },
    queryFn: async (): Promise<SavedPipeline[]> => {
      const listed = (await listStructured(projectId, owner)).filter(
        isPipeline
      )
      return Promise.all(
        listed.map(async (entry): Promise<SavedPipeline> => {
          const record = await getStructured(projectId, owner, entry.name)
          const meta = isPipeline(record) ? record.meta : entry.meta
          return {
            name: entry.name,
            // The record's name is its file name, as in the console.
            pipeline: { ...meta.pipeline, name: entry.name },
            script: record.script,
          }
        })
      )
    },
  })
}
